import { TemplateResult, html, unsafeCSS } from 'lit';
import { Task } from '@lit/task';
import { state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ContextConsumer } from '@lit/context';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafAlert } from '@cigna/dcws_leaf-web-components/components/alert/alert.js';
import { LeafSpinner } from '@cigna/dcws_leaf-web-components/components/spinner/spinner.component.js';
import { I18nMixin } from '../../utils/i18n-mixin.js';
import { endpointsContext } from '../contexts.js';
import styles from './pdl-viewer.scss?inline';
import { pdlfilefetch, pdljsonfetch } from './util/fetch.js';
import type { pdlViewerSegmentLocale, pdljson } from './interfaces.js';

export class CHCpdlViewer extends ScopedRegistryHost(I18nMixin(LeafElement)) {
  private overRideStyles = unsafeCSS(styles);
  static elementDefinitions = {
    'leaf-spinner': LeafSpinner,
    'leaf-alert': LeafAlert,
  };
  protected override ns = 'pdl-viewer';
  // left for locale wiring purposes
  private pdlViewerSegmentLocale?: pdlViewerSegmentLocale;

  @state()
  initialView = true;

  private fetchpathing?: string;

  private env = '';

  private pdljsonlocation?: string;

  @state()
  pdljson?: pdljson;

  override onNamespacesLoaded = () => {
    this.pdlViewerSegmentLocale = this.i18n?.getLanguageData(this.ns) as pdlViewerSegmentLocale;
  };

  constructor() {
    super();
    new ContextConsumer(this, {
      context: endpointsContext,
      callback: (endpoints) => {
        this.fetchpathing = endpoints.pdlViewerDomain[0];
        this.env = endpoints.pdlViewerDomain[1];
        this.pdljsonlocation = endpoints.pdlViewerDomain[2];
      },
    });
  }

  private _pdljsonfetchTask = new Task(this, {
    task: async ([pdljsonlocation]) => {
      const tagruntime = document.createElement('script');
      tagruntime.type = 'module';
      const tagpolyfills = document.createElement('script');
      tagpolyfills.type = 'module';
      const tagvendor = document.createElement('script');
      tagvendor.type = 'module';
      const tagmain = document.createElement('script');
      tagmain.type = 'module';
      const div = document.createElement('div');

      try {
        this.pdljson = (await pdljsonfetch(`${pdljsonlocation}`)) as unknown as pdljson;
        tagruntime.innerHTML = await pdlfilefetch(`${this.fetchpathing}${this.pdljson?.[this.env]?.runtime}`);
        tagpolyfills.innerHTML = await pdlfilefetch(`${this.fetchpathing}${this.pdljson?.[this.env]?.polyfills}`);
        tagvendor.innerHTML = await pdlfilefetch(`${this.fetchpathing}${this.pdljson?.[this.env]?.vendor}`);
        tagmain.innerHTML = await pdlfilefetch(`${this.fetchpathing}${this.pdljson?.[this.env]?.main}`);

        div.appendChild(tagruntime);
        div.appendChild(tagpolyfills);
        div.appendChild(tagvendor);
        div.appendChild(tagmain);
      } catch (e) {
        throw new Error('error');
      }
      return div;
    },
    args: () => [`${this.pdljsonlocation}`],
  });

  pdlViewerContainer(): TemplateResult {
    return html`
      ${this._pdljsonfetchTask.render({
        pending: () => html`<leaf-spinner size="small">${this.pdlViewerSegmentLocale?.loading}</leaf-spinner>`,
        complete: (scriptTags) =>
          html`<cigna-root></cigna-root>
            <style>
              ${this.overRideStyles}
            </style>
            ${scriptTags}`,
        error: () =>
          html`<leaf-alert variant="standalone" type="error" open>
            <h6 slot="heading">${this.pdlViewerSegmentLocale?.pdlLocFileError.header}</h6>
            ${this.pdlViewerSegmentLocale?.pdlLocFileError.body}
          </leaf-alert>`,
      })}
    `;
  }

  override render() {
    const componentClassName = {
      'chcapp-pdl-viewer': true,
    };

    return html` <div class=${classMap(componentClassName)}>${this.pdlViewerContainer()}</div>`;
  }

  // overrides lit-element's behavior so that the content wont render in shadow dom
  // Neccessary for angular mono repo framework
  override createRenderRoot() {
    return this;
  }
}
